import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import Button from "@mui/joy/Button";
import CustomModal from "../../common/CustomModal";
import { useMutation, useQuery } from "@tanstack/react-query";
import useRemoveTab from "hooks/useRemoveTab";
import {
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_STATUS_DETAIL_ENDPOINT,
  thoughtRecordApi,
} from "api/thoughtRecordApi";
import { theme } from "styles/theme";
import { TrashIconSvg } from "static/image/common/SvgAssets";
import { meditationRecordApi } from "../../../api/meditationRecordApi";
import useRefetchTaskList from "../../../hooks/useRefetchTaskList";
import { userAtom } from "../../../recoil/userAtom";
import { useRecoilValue } from "recoil";

type ButtonType = "thought_record" | "meditation_record" | "task";
export default function TaskRemoveButton({
  taskKey,
  thoughtRecordKey,
  meditationRecordKey,
  onClose,
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  meditationRecordKey?: string;
  onClose: () => void;
}) {
  const refetchTaskList = useRefetchTaskList();
  const { removeTab } = useRemoveTab({ taskKey });
  const user = useRecoilValue(userAtom);

  const [buttonType, setButtonType] = useState<ButtonType>(
    thoughtRecordKey !== undefined
      ? "thought_record"
      : meditationRecordKey !== undefined
        ? "meditation_record"
        : "task"
  );

  const [open, setOpen] = useState(false);
  const [isThoughtRecordNotRemovable, setIsThoughtRecordNotRemovable] = useState<boolean>(true);

  const getStatusQueryFn = useCallback(
    () => thoughtRecordApi.getStatus(thoughtRecordKey!!),
    [thoughtRecordKey]
  );

  useQuery(
    [THOUGHTRECORD_DOMAIN, THOUGHTRECORD_STATUS_DETAIL_ENDPOINT, user?.userId],
    getStatusQueryFn,
    {
      enabled: !!user?.userId && !!thoughtRecordKey,
      onSuccess: (data) => {
        setIsThoughtRecordNotRemovable(data.result?.undoneAt !== null);
      },
    }
  );

  const { mutate: thoughtRecordRemove } = useMutation(
    () => thoughtRecordApi.remove(`${thoughtRecordKey}`),
    {
      onSuccess: () => {
        refetchTaskList();
        removeTab();
      },
    }
  );

  const { mutate: meditationRecordRemove } = useMutation(
    () => meditationRecordApi.remove(`${meditationRecordKey}`),
    {
      onSuccess: () => {
        refetchTaskList();
        removeTab();
      },
    }
  );

  const handleRemove = () => {
    if (buttonType === "thought_record") {
      thoughtRecordRemove();
    } else if (buttonType === "meditation_record") {
      meditationRecordRemove();
    } else {
      // ignore
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="Plain"
        color="neutral"
        disabled={buttonType === "task" || isThoughtRecordNotRemovable}
        startDecorator={<TrashIconSvg />}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{
          "--Button-gap": "12px",
          width: "100%",
          height: "44px",
          fontSize: "16px",
          fontWeight: 500,
          pl: "20px",
          pr: "24px",
          justifyContent: "flex-start",
          color: theme.vars.palette.Content.Strong,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        {"삭제하기"}
      </Button>
      <CustomModal
        open={open}
        onClose={() => {
          setOpen(false);
          onClose();
        }}
        title={`${buttonType === "thought_record" ? "기록을" : buttonType === "meditation_record" ? "훈련을" : "활동지를"} 삭제할까요?`}
        subtitle={"삭제하면 복구할 수 없어요."}
        actions={[
          {
            label: "삭제하기",
            onClick: () => {
              handleRemove();
              setOpen(false);
              onClose();
            },
            variant: "Solid",
            color: "danger",
          },
        ]}
      />
    </>
  );
}
